import { lazy, Suspense, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { replaceModalStack, selectModalStack } from '../../store/modalSlice'
import {
  getModalHistoryIds,
  notifyClosedModals,
  resolveModalEntries,
  resolveModalEntry,
  withModalHistoryIds,
} from '../../services/modalHistory'

const AlertModal = lazy(() => import('../../components/modals/AlertModal'))
const ConfirmationModal = lazy(() => import('../../components/modals/ConfirmationModal'))
const DailyLogFormModal = lazy(() => import('../../features/daily-log/modals/DailyLogFormModal'))
const AttendanceProofModal = lazy(() => import('../../features/attendance-proof/modals/AttendanceProofModal'))
const SessionalPlanFormModal = lazy(() => import('../../features/planner/modals/SessionalPlanFormModal'))
const WeeklyQuantityDelayModal = lazy(() => import('../../features/planner/modals/WeeklyQuantityDelayModal'))
const DailyQuantityDelayModal = lazy(() => import('../../features/planner/modals/DailyQuantityDelayModal'))
const QuantityAmountEditModal = lazy(() => import('../../features/planner/modals/QuantityAmountEditModal'))
const ReportTypeSelectionModal = lazy(() => import('../../features/reports/modals/ReportTypeSelectionModal'))
const WrittenReportSelectionModal = lazy(() => import('../../features/reports/modals/WrittenReportSelectionModal'))
const AccountWithdrawalModal = lazy(() => import('../../features/profile/modals/AccountWithdrawalModal'))
const PasswordChangeModal = lazy(() => import('../../features/profile/modals/PasswordChangeModal'))

const MODAL_COMPONENTS = {
  alert: AlertModal,
  confirm: ConfirmationModal,
  dailyLogForm: DailyLogFormModal,
  attendanceProof: AttendanceProofModal,
  sessionalPlanForm: SessionalPlanFormModal,
  weeklyQuantityDelay: WeeklyQuantityDelayModal,
  dailyQuantityDelay: DailyQuantityDelayModal,
  quantityAmountEdit: QuantityAmountEditModal,
  reportTypeSelection: ReportTypeSelectionModal,
  writtenReportSelection: WrittenReportSelectionModal,
  accountWithdrawal: AccountWithdrawalModal,
  passwordChange: PasswordChangeModal,
}

function ModalContainer() {
  const dispatch = useDispatch()
  const modalList = useSelector(selectModalStack)
  const location = useLocation()
  const navigate = useNavigate()
  const previousHistoryIdsRef = useRef(getModalHistoryIds(location.state))

  useEffect(() => {
    const historyIds = getModalHistoryIds(location.state)
    const resolvedEntries = resolveModalEntries(historyIds)
    const resolvedHistoryIds = resolvedEntries.map(({ id }) => id)
    const modalIds = modalList.map(({ id }) => id)
    notifyClosedModals(previousHistoryIdsRef.current, historyIds)
    previousHistoryIdsRef.current = historyIds

    if (resolvedHistoryIds.length !== historyIds.length) {
      navigate(
        { pathname: location.pathname, search: location.search, hash: location.hash },
        { replace: true, state: withModalHistoryIds(location.state, resolvedHistoryIds) },
      )
      return
    }

    if (historyIds.length !== modalIds.length
      || historyIds.some((id, index) => id !== modalIds[index])) {
      dispatch(replaceModalStack(resolvedEntries))
    }
  }, [dispatch, location.hash, location.key, location.pathname, location.search, location.state, modalList, navigate])

  const renderModal = modalList.map(({ id, type }, index) => {
    const ModalComponent = MODAL_COMPONENTS[type]
    const entry = resolveModalEntry(id)
    if (!ModalComponent || !entry) return null
    return <ModalComponent key={id || `${type}-${index}`} { ...entry.props } />
  })
  return createPortal(
    <Suspense fallback={null}>{renderModal}</Suspense>
    , document.getElementById("modal")
  )
}

export default ModalContainer
